/**
 * One Spotify player per slot for the whole app session (Sam, 2026-10-20: the preview "loads with a small delay").
 *
 * A fresh <iframe src="open.spotify.com/embed/..."> boots Spotify's whole player - HTML, scripts, artist data - every time
 * a sheet opens: about 1.1 s before anything shows. Spotify's iFrame API keeps a player alive and switches it to another
 * artist with loadUri in about 0.2 s (measured 2026-10-20). So the players live here, outside React, in fixed hosts on
 * <body>, and a sheet's SpotifyPreview only lays its slot over its own placeholder while it is open. Moving an iframe in
 * the DOM reloads it, which is why the player floats over the sheet instead of being placed in it.
 *
 * No Spotify Web API quota is used: this is Spotify's own embed, the same as before, booted once instead of per tap.
 * Slot i serves the i-th preview on a sheet (a b2b set shows two or three), so at most that many players ever exist.
 */

import { appLog } from './appLog';
import { isEffectivelyUnreachable } from './network/effectiveConnectivity';

const API_SRC = 'https://open.spotify.com/embed/iframe-api/v1';
const HOST_Z = '91'; // above the modal backdrop and panel (tailwind z-modal-backdrop: 90)

let apiPromise = null;
let barSlot = null;
function loadApi() {
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve, reject) => {
    const prev = window.onSpotifyIframeApiReady;
    window.onSpotifyIframeApiReady = (api) => {
      prev?.(api);
      resolve(api);
    };
    const s = document.createElement('script');
    s.src = API_SRC;
    s.async = true;
    s.onerror = () => {
      apiPromise = null;
      reject(new Error('Spotify iFrame API failed to load'));
    };
    document.head.appendChild(s);
  });
  return apiPromise;
}

const slots = [];

function makeSlot(z = HOST_Z) {
  const host = document.createElement('div');
  Object.assign(host.style, {
    position: 'fixed',
    left: '0px',
    top: '0px',
    width: '0px',
    height: '80px',
    zIndex: z,
    borderRadius: '12px',
    overflow: 'hidden',
    visibility: 'hidden',
    pointerEvents: 'none',
  });
  host.setAttribute('aria-hidden', 'true');
  const mount = document.createElement('div');
  host.appendChild(mount);
  document.body.appendChild(host);
  const slot = {
    host,
    mount,
    controller: null,
    uri: null,
    readyUri: null,
    fallback: 0,
    creating: null,
    owner: null,
    listeners: new Set(),
    paused: true,
    playWhenReady: false,
  };
  return slot;
}
function makePooledSlot() {
  const slot = makeSlot();
  slots.push(slot);
  return slot;
}

const notify = (slot) => {
  for (const fn of slot.listeners) fn();
};
/** Shown only once Spotify says the artist asked for is in: mid-switch the embed is a white box, then the old artist. */
export const slotReady = (slot) => slot.readyUri != null && slot.readyUri === slot.uri;
// spotify.preview: how long from asking for an artist to showing it, and whether Spotify's 'ready' said so or the fallback
// timer did (2026-10-21: still slow on the phone; if iOS never sends 'ready' after loadUri, every preview waits 1.5 s).
function markReady(slot, via = 'ready') {
  clearTimeout(slot.fallback);
  if (slot.readyUri === slot.uri) return;
  slot.readyUri = slot.uri;
  notify(slot);
  if (slot.playWhenReady) {
    slot.playWhenReady = false;
    try {
      slot.controller?.play();
    } catch {
      /* the bar's own play button still works */
    }
  }
  if (slot.askedAt) {
    appLog('info', 'spotify.preview', {
      ms: Math.round(performance.now() - slot.askedAt),
      via,
      boot: Boolean(slot.boot),
    });
    slot.askedAt = 0;
    slot.boot = false;
  }
}

/** Point slot i at an artist. The first call boots the player; later calls switch it in place. */
function load(slot, uri) {
  if (slot.controller) {
    if (slot.uri !== uri) {
      slot.uri = uri;
      slot.askedAt = performance.now();
      slot.boot = false;
      notify(slot);
      slot.controller.loadUri(uri);
      clearTimeout(slot.fallback);
      // Never stuck behind the shimmer - but only with a connection: offline the new artist never arrives, and marking it
      // ready anyway played whatever the embed still held, the last artist under the new name (Sam, 2026-11-04).
      slot.fallback = setTimeout(() => {
        if (isEffectivelyUnreachable()) slot.playWhenReady = false;
        else markReady(slot, 'fallback');
      }, 1500);
    }
    return;
  }
  slot.uri = uri;
  slot.askedAt = performance.now();
  slot.boot = true;
  if (slot.creating) return;
  slot.creating = loadApi()
    .then(
      (api) =>
        new Promise((resolve) => {
          api.createController(
            slot.mount,
            { uri, width: '100%', height: 80, theme: 'dark' },
            (controller) => {
              slot.controller = controller;
              controller.addListener('ready', () => markReady(slot)); // fires on boot and again after each loadUri
              // Our buttons and Spotify's own follow one state: whichever is pressed, this reports it. One sound at a time: a
              // player that starts pauses every other (the bar and a sheet's player are separate embeds).
              controller.addListener('playback_update', (e) => {
                const paused = Boolean(e?.data?.isPaused);
                if (!paused)
                  for (const o of [...slots, barSlot])
                    if (o && o !== slot && !o.paused) {
                      try {
                        o.controller?.pause();
                      } catch {
                        /* not started */
                      }
                    }
                if (paused !== slot.paused) {
                  slot.paused = paused;
                  notify(slot);
                }
              });
              if (slot.uri !== uri) controller.loadUri(slot.uri); // a different artist was asked for while it booted
              resolve();
            },
          );
        }),
    )
    .catch(() => {
      slot.creating = null;
    });
}

/** Boot slot 0 ahead of the first tap (the Sets page calls this with an artist it shows), so even the first sheet is quick. */
export function warmSpotifyPlayer(spotifyId) {
  if (!spotifyId || typeof document === 'undefined') return;
  const slot = slots[0] ?? makePooledSlot();
  if (!slot.controller && !slot.creating) load(slot, `spotify:artist:${spotifyId}`);
}

/** Switch slot 0 to an artist as a finger lands on its row, before the sheet opens: the switch is under way by the time the
 *  sheet asks for it (claimSpotifyPlayer then finds the same artist and does nothing). Hidden, never played. */
export function primeSpotifyPlayer(spotifyId) {
  if (!spotifyId || typeof document === 'undefined') return;
  const slot = slots[0] ?? makePooledSlot();
  if (slot.owner) return; // a sheet is showing slot 0
  load(slot, `spotify:artist:${spotifyId}`);
}

/**
 * Claim slot i for an artist while a sheet shows it. Returns { slot, release }. The caller positions slot.host over its
 * placeholder (placeSlot) and calls release on unmount, which hides and pauses it.
 */
export function claimSpotifyPlayer(i, spotifyId) {
  const slot =
    slots[i] ??
    (() => {
      while (slots.length < i) makePooledSlot();
      return makePooledSlot();
    })();
  const token = {};
  slot.owner = token;
  load(slot, `spotify:artist:${spotifyId}`);
  return {
    slot,
    release() {
      if (slot.owner !== token) return;
      slot.owner = null;
      slot.host.style.visibility = 'hidden';
      slot.host.style.pointerEvents = 'none';
      try {
        slot.controller?.pause();
      } catch {
        /* the embed may not have started */
      }
    },
  };
}

/** Lay the slot's player exactly over el, clipped to the scrolling box el sits in, and show it once the artist is in. */
export function placeSlot(slot, el) {
  const r = el.getBoundingClientRect();
  let clip = null;
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if (oy === 'auto' || oy === 'scroll' || oy === 'hidden') {
      clip = p.getBoundingClientRect();
      break;
    }
  }
  const h = slot.host.style;
  h.left = `${r.left}px`;
  h.top = `${r.top}px`;
  h.width = `${r.width}px`;
  h.height = `${r.height}px`;
  // Spotify's embed lays itself out by its own height (80 px: one line; 152 px: the artwork and a big Play).
  const frame = slot.host.querySelector('iframe');
  if (frame && frame.style.height !== h.height) frame.style.height = h.height;
  if (clip) {
    const t = Math.max(0, clip.top - r.top),
      b = Math.max(0, r.bottom - clip.bottom);
    h.clipPath = t || b ? `inset(${t}px 0 ${b}px 0)` : '';
    const hidden = t + b >= r.height;
    h.visibility = slotReady(slot) && !hidden ? 'visible' : 'hidden';
    h.pointerEvents = slotReady(slot) && !hidden ? 'auto' : 'none';
  } else {
    h.clipPath = '';
    h.visibility = slotReady(slot) ? 'visible' : 'hidden';
    h.pointerEvents = slotReady(slot) ? 'auto' : 'none';
  }
}

export function onSlotChange(slot, fn) {
  slot.listeners.add(fn);
  return () => slot.listeners.delete(fn);
}

/**
 * The now-playing bar (Sam, 2026-10-23): a row's play button plays the artist here, in Spotify's own player docked above
 * the tab bar, without opening the sheet. Its own slot (under the sheet's z-index), so a sheet opening over it never
 * takes it away. A b2b set keeps all its artists and plays one at a time (`index`); the bar shows them as tabs.
 */
let bar = null; // { key, artists: [{ spotifyId, name }], index }
let barSnapshot = { key: null, artists: [], index: 0, paused: true, ready: false };
const barListeners = new Set();
function emitBar() {
  const slot = barSlot;
  barSnapshot = bar
    ? {
        key: bar.key,
        artists: bar.artists,
        index: bar.index,
        paused: slot ? slot.paused : true,
        ready: slot ? slotReady(slot) : false,
      }
    : { key: null, artists: [], index: 0, paused: true, ready: false };
  for (const fn of barListeners) fn();
}
function getBarSlot() {
  if (!barSlot) {
    barSlot = makeSlot('48');
    barSlot.listeners.add(emitBar);
  }
  return barSlot;
}
export function subscribeBar(fn) {
  barListeners.add(fn);
  return () => barListeners.delete(fn);
}
export function getBar() {
  return barSnapshot;
}
export function barSlotFor() {
  return getBarSlot();
}

/** With no connection a play tap opens nothing and says why (Sam, 2026-11-04); the app's bar shows the toast. */
const offlineListeners = new Set();
export function onPreviewOffline(fn) {
  offlineListeners.add(fn);
  return () => offlineListeners.delete(fn);
}

/** Play a row's artist (or the index-th artist of a b2b) in the bar; the same row again toggles play and pause. */
export function playInBar(key, artists, index = 0) {
  const list = (artists || []).filter((a) => a?.spotifyId);
  if (!list.length || typeof document === 'undefined') return;
  const slot = getBarSlot();
  const same = bar && bar.key === key && bar.index === index;
  // The artist already in the player plays offline (Spotify keeps its preview); anything else cannot load.
  if (isEffectivelyUnreachable() && !(same && slotReady(slot))) {
    for (const fn of offlineListeners) fn();
    return;
  }
  if (same && slotReady(slot)) {
    try {
      slot.controller?.togglePlay();
    } catch {
      /* the bar's own button still works */
    }
    return;
  }
  bar = { key, artists: list, index: Math.min(index, list.length - 1) };
  const uri = `spotify:artist:${bar.artists[bar.index].spotifyId}`;
  if (slot.uri === uri && slotReady(slot)) {
    try {
      slot.controller?.play();
    } catch {
      /* as above */
    }
  } else {
    slot.paused = true;
    slot.playWhenReady = true;
    load(slot, uri);
  } // playing once Spotify says so
  emitBar();
}
/** One tap on a row or a set (Sam, 2026-10-23): the artist goes into the bar, ready, without playing - an accidental tap
 *  never starts sound. Only the play button (or Spotify's own) plays. */
export function loadInBar(key, artists, index = 0) {
  const list = (artists || []).filter((a) => a?.spotifyId);
  if (!list.length || typeof document === 'undefined') return;
  if (bar && bar.key === key) return;
  const slot = getBarSlot();
  if (!slot.paused) {
    try {
      slot.controller?.pause();
    } catch {
      /* not started */
    }
  }
  bar = { key, artists: list, index: Math.min(index, list.length - 1) };
  slot.playWhenReady = false;
  // Paused from this moment, not when Spotify's report arrives: the new row showed the moving bars with no sound until
  // then (Sam, 2026-10-23). The bars come back only when Spotify reports it playing.
  slot.paused = true;
  load(slot, `spotify:artist:${bar.artists[bar.index].spotifyId}`);
  emitBar();
}
export function toggleBar() {
  try {
    barSlot?.controller?.togglePlay();
  } catch {
    /* not started */
  }
}
export function closeBar() {
  try {
    barSlot?.controller?.pause();
  } catch {
    /* not started */
  }
  if (barSlot) {
    barSlot.host.style.visibility = 'hidden';
    barSlot.host.style.pointerEvents = 'none';
  }
  bar = null;
  emitBar();
}
